import Sidebar from '../../components/Sidebar';
import { Header } from '../../components/header';
import { DeductionINvoiceSection } from '../../components/deduction-invoice-section.component';
import DeductionFilters from '../../components/deduction-filters.component';
import DeductionTabs from '../../components/deduction-tabs.component';
import AddDeductionButton from '../../components/add-deduction-button.component';
import NotInvoicedSection from '../../components/not-invoiced-section.component';
import AddDeductionModal from '../../components/add-deduction-modal.component';
import { useTranslation } from 'react-i18next';
import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks/useRedux';
import { fetchInvoiceEditData } from '../../features/customer/customer.slice';
import { fetchTimesheetDeductions } from '../../features/timesheet/timesheet.slice';
import { useForm } from 'react-hook-form';
import { useDeductionPageSubmit } from '../../hooks/useDeductionPageSubmit.hook';
import './deduction.page.scss';

function DeductionPage() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('invoiced');

    // Get userId and customers from Redux
    const userId = useSelector((state: any) => state.auth?.user?.userId);

    // Clear filters when switching tabs
    useEffect(() => {
        // Set invoicing status based on active tab
        if (activeTab === 'invoiced') {
            setSelectedYear('invoiced');
        } else if (activeTab === 'not-invoiced') {
            setSelectedYear('non-invoiced');
        }
        setSelectedMonth('all');
        setSelectedClient('all');
        setSearchInput('');
        setStartDate('');
        setEndDate('');
    }, [activeTab]);
    const { customers, loading: customersLoading } = useSelector((state: any) => state.customer || { customers: [], loading: false });
    const globalData = useSelector((state: any) => state?.globalData?.data);

    // Filter states
    const [selectedYear, setSelectedYear] = useState('all');
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [selectedClient, setSelectedClient] = useState('all');
    const [searchInput, setSearchInput] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isLoadingDeductions, setIsLoadingDeductions] = useState(false);
    const [invoicedLoadTrigger, setInvoicedLoadTrigger] = useState(0);

    // Search should only be considered when input length is more than 3
    const effectiveSearchQuery = useMemo(() => {
        const q = searchInput.trim();
        return q.length > 3 ? q : '';
    }, [searchInput]);

    // Modal state management
    const [showDeductionModal, setShowDeductionModal] = useState(false);
    const [editingDeductionData, setEditingDeductionData] = useState<any | null>(null);
    const [selectedCustomerForDeduction, setSelectedCustomerForDeduction] = useState<number | null>(null);

    // React Hook Form setup
    const {
        register,
        setValue,
        watch,
        formState: { errors },
        trigger,
        reset
    } = useForm();

    // Default preferences for deduction modal
    const preferences = useMemo(() => ({
        vat: 25,
        currency: 'SEK'
    }), []);

    // Use deduction submit hook
    const { handleDeductionSubmit } = useDeductionPageSubmit({
        editingDeductionData,
        selectedCustomerId: selectedCustomerForDeduction,
        preferences,
        globalData,
        userId: userId || 0
    });

    // Fetch customers on component mount
    useEffect(() => {
        if (userId) {
            dispatch(fetchInvoiceEditData(userId));
        }
    }, [userId, dispatch]);

    // Convert customers to dropdown options
    const customerOptions = useMemo(() => {
        return customers.map((customer: any) => ({
            value: String(customer.customerId || customer.id),
            label: customer.name || customer.customerName
        }));
    }, [customers]);

    // Tabs configuration
    const tabs = useMemo(() => [
        { key: 'invoiced', label: t('INVOICED') || 'Invoiced' },
        { key: 'not-invoiced', label: t('NOT_INVOICED') || 'Not Invoiced' }
    ], [t]);

    // Modal handlers
    const handleOpenModal = () => {
        setEditingDeductionData(null);
        setSelectedCustomerForDeduction(null);
        reset(); // Reset form
        setShowDeductionModal(true);
    };

    const handleOpenEditModal = (deductionData: any) => {
        setEditingDeductionData(deductionData);
        setSelectedCustomerForDeduction(deductionData.customerId || null);
        setShowDeductionModal(true);
    };

    const handleCloseModal = () => {
        setShowDeductionModal(false);
        setEditingDeductionData(null);
        setSelectedCustomerForDeduction(null);
        reset(); // Reset form
    };

    // Wrapper: on success → close modal + re-fetch the not-invoiced list
    const handleModalSubmit = async (deductionData: any) => {
        await handleDeductionSubmit(deductionData);
        // Success — close modal then refresh the list
        handleCloseModal();

        // Build params object - only include parameters that are actually selected
        const params: any = {
            pageNumber: 1,
            tab: 2 // Not invoiced tab
        };

        // Only add fromDate and toDate if both dates are selected
        if (startDate && startDate.trim() !== '' && endDate && endDate.trim() !== '') {
            params.fromDate = startDate;
            params.toDate = endDate;
        } else {
            if (selectedYear && selectedYear !== 'all' && selectedYear !== 'invoiced' && selectedYear !== 'non-invoiced') {
                const yearNum = parseInt(selectedYear);
                const monthNum = selectedMonth && selectedMonth !== 'all' ? parseInt(selectedMonth) : 1;
                params.year = `${yearNum}-${String(monthNum).padStart(2, '0')}-01T00:00:00`;
            }

            if (selectedMonth && selectedMonth !== 'all' && selectedYear && selectedYear !== 'all' && selectedYear !== 'invoiced' && selectedYear !== 'non-invoiced') {
                const yearNum = parseInt(selectedYear);
                const monthNum = parseInt(selectedMonth);
                params.month = `${yearNum}-${String(monthNum).padStart(2, '0')}-01T00:00:00`;
            }
        }

        // Only add customerId if a specific customer is selected
        if (selectedClient && selectedClient !== 'all' && selectedClient.trim() !== '') {
            params.customerId = selectedClient;
        }

        // Only add searchQuery if it has a value
        if (effectiveSearchQuery) {
            params.searchQuery = effectiveSearchQuery;
        }

        dispatch(fetchTimesheetDeductions(params));
    };

    const handleCustomerSelect = (customerId: number) => {
        setSelectedCustomerForDeduction(customerId);
    };

    // Handle invoicing status change (this controls the tab switching)
    const handleInvoicingStatusChange = (value: string) => {
        setSelectedYear(value);
        // Switch tabs based on invoicing status
        if (value === 'invoiced') {
            setActiveTab('invoiced');
        } else if (value === 'non-invoiced') {
            setActiveTab('not-invoiced');
        }
    };

    // Load button is enabled only when both dates are selected
    const isDateRangeSelected = Boolean(startDate?.trim() && endDate?.trim());

    // Load deductions based on current filters - only called on button click
    const handleLoadDeductions = async () => {
        if (!isDateRangeSelected) {
            return;
        }

        setIsLoadingDeductions(true);
        try {
            // Load deductions using date range filter only
            const params: any = {
                pageNumber: 1,
                tab:2,
                fromDate: startDate,
                toDate: endDate
            };
            if (activeTab === 'invoiced') {
                setInvoicedLoadTrigger(prev => prev + 1);
            } else {
                await dispatch(fetchTimesheetDeductions(params)).unwrap();
            }

        } catch (error) {
            console.error('Error loading deductions:', error);
        } finally {
            setIsLoadingDeductions(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'invoiced':
                return <DeductionINvoiceSection
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedClient={selectedClient}
                    searchQuery={effectiveSearchQuery}
                    loadTrigger={invoicedLoadTrigger}
                    fromDate={startDate}
                    toDate={endDate}
                />;
            case 'not-invoiced':
                return <NotInvoicedSection
                    onEditDeduction={handleOpenEditModal}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    customerId={selectedClient}
                    searchQuery={effectiveSearchQuery}
                />;
            default:
                return <DeductionINvoiceSection
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedClient={selectedClient}
                    searchQuery={effectiveSearchQuery}
                    loadTrigger={invoicedLoadTrigger}
                    fromDate={startDate}
                    toDate={endDate}
                />;
        }
    };

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar
                    activeItemId='deduction-invoice'
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                <div className="dashboard-main-content">
                    <Header
                        setIsSidebarOpen={setIsSidebarOpen}
                    />
                    <div className='px-3'>
                        <h3 className="dashboard-page__title">{t('DEDUCTIONS_LABEL')}</h3>

                        <p className="deduction-page__description">
                            {t('DEDUCTION_DESCRIPTION') || 'Here are all the deductions made via invoices created through Invozio.'}
                        </p>

                        <div className="deduction-page__filters-row">
                            <DeductionFilters
                                selectedYear={selectedYear}
                                selectedMonth={selectedMonth}
                                selectedClient={selectedClient}
                                searchQuery={searchInput}
                                startDate={startDate}
                                endDate={endDate}
                                onYearChange={handleInvoicingStatusChange}
                                onMonthChange={setSelectedMonth}
                                onClientChange={setSelectedClient}
                                onSearchChange={(e) => setSearchInput(e.target.value)}
                                onStartDateChange={setStartDate}
                                onEndDateChange={setEndDate}
                                clients={customerOptions}
                                loading={customersLoading}
                            />
                            <div className="deduction-page__actions">
                                <button 
                                    className="load-deduction-btn"
                                    onClick={handleLoadDeductions}
                                    disabled={isLoadingDeductions || !isDateRangeSelected}
                                >
                                    {isLoadingDeductions ? (
                                        <span className="load-deduction-btn__text">{t('LOADING') || 'Loading...'}</span>
                                    ) : (
                                        <>
                                            <span className="load-deduction-btn__text">{t('LOAD_DEDUCTIONS') || 'Load Deductions'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                            <div className="divider-deductions"></div>
                            <div className="deduction-page__actions">
                                <AddDeductionButton onClick={handleOpenModal} />
                            </div>
                        </div>



                        <div className="deduction-page__content">
                            {renderTabContent()}
                        </div>
                    </div>

                </div>
            </div>

            {/* Add Deduction Modal */}
            <AddDeductionModal
                isOpen={showDeductionModal}
                onClose={handleCloseModal}
                onSubmit={handleModalSubmit}
                register={register}
                setValue={setValue}
                watch={watch}
                errors={errors}
                trigger={trigger}
                t={t}
                editingDeductionData={editingDeductionData}
                preferences={preferences}
                showClientSelector={true}
                customers={customerOptions}
                selectedCustomerId={selectedCustomerForDeduction}
                onCustomerChange={handleCustomerSelect}
            />
        </div>
    );
}

export default DeductionPage;
